/*
 * Sprint C — UPI "coming soon" top-up sheet (Prompt 7).
 *
 * This bottom sheet opens on every 402 INSUFFICIENT_CREDITS response (the
 * axios interceptor in useAuth.js dispatches a window event). We keep the
 * UI honest: the user sees their current balance, exactly how many more
 * credits they need, and a "Pay with UPI — coming next release" button
 * that is disabled with a tooltip. No fake payment flow.
 */
import React, { useEffect, useState } from "react";

const INK      = "#1C1C1E";
const MUTE     = "#86868B";
const INDIGO   = "#5D00FF";
const FUCHSIA  = "#FF2E9A";

export default function CreditsTopupSheet() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ balance: 0, needed: 0, message: "" });

  useEffect(() => {
    const handler = (ev) => {
      setData({
        balance: ev.detail?.balance ?? 0,
        needed:  ev.detail?.needed  ?? 0,
        message: ev.detail?.message || "You need more Credits to continue.",
      });
      setOpen(true);
    };
    window.addEventListener("kalki:credits-topup-needed", handler);
    return () => window.removeEventListener("kalki:credits-topup-needed", handler);
  }, []);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(10,10,15,0.55)", backdropFilter: "blur(6px)" }}
      onClick={() => setOpen(false)}
      data-testid="credits-topup-sheet"
    >
      <div
        className="w-full sm:w-[480px] bg-white rounded-t-3xl sm:rounded-3xl p-6 sm:p-7"
        style={{ boxShadow: "0 -20px 60px rgba(0,0,0,0.25)", paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-[0.3em] mb-2" style={{ color: INDIGO }}>
          ✦ CREDITS NEEDED
        </div>
        <div className="text-xl font-medium mb-3" style={{ color: INK }}>
          {data.message}
        </div>
        <div
          className="rounded-2xl p-4 mb-4"
          style={{
            background: "linear-gradient(135deg, rgba(93,0,255,0.06), rgba(255,46,154,0.04))",
            border: "1px solid rgba(93,0,255,0.14)",
          }}
        >
          <div className="flex items-center justify-between text-sm mb-2" style={{ color: MUTE }}>
            <span>Your balance</span>
            <span data-testid="topup-balance" style={{ color: INK, fontWeight: 500 }}>
              {data.balance} credits
            </span>
          </div>
          <div className="flex items-center justify-between text-sm" style={{ color: MUTE }}>
            <span>You need</span>
            <span data-testid="topup-needed" style={{ color: INK, fontWeight: 500 }}>
              {data.needed} more credits
            </span>
          </div>
        </div>

        <button
          disabled
          data-testid="topup-upi-cta"
          title="UPI top-up launches with the next release"
          className="w-full py-3 rounded-full text-[13px] tracking-[0.25em] opacity-60 cursor-not-allowed"
          style={{
            background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
            color: "white",
          }}
        >
          PAY WITH UPI · COMING SOON
        </button>
        <div className="text-[11px] mt-3 text-center" style={{ color: MUTE }}>
          Instant UPI top-up launches in the next release. Share your referral link in the Me tab to earn credits in the meantime.
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="topup-dismiss"
          className="mt-4 w-full py-2 rounded-full text-[12px]"
          style={{ background: "rgba(28,28,28,0.05)", color: INK }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
